import React from 'react';
import { useTranslation } from 'react-i18next';
import { ZfBox, ZfLoadingSpinner } from '@zafire/react';
import { ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import type { SaveState } from '../../utils/saveGate';
import { getProductDisplayName } from '../../../mappers/serviceAgreementToTreeMapper';
import { useSafeDepositBoxServiceRoleSection } from '../hooks/useSafeDepositBoxServiceRoleSection';
import { ConfigureProductModalBanner } from './ConfigureProductModalBanner';
import { PartnerSearchWidget } from './PartnerSearchWidget';
import { SafeDepositBoxConfigurationSection } from './SafeDepositBoxConfigurationSection';
import { SafeDepositBoxServiceRolesGrid } from './SafeDepositBoxServiceRolesGrid';
import { ZfDividerTitle } from '../../../../../components/common/ZfDividerTitle';

type Props = {
  serviceAgreement: ServiceAgreementV2;
  brUid: string;
  onSaveStateChange: (state: SaveState) => void;
};

export const SafeDepositBoxServiceRoleSection: React.FC<Props> = ({ serviceAgreement, brUid, onSaveStateChange }) => {
  const { t } = useTranslation();
  const {
    rows,
    safeRoleOptions,
    signatureAuthorityOptions,
    selectedPartners,
    handleSelectedPartnersChange,
    updateRow,
    removeRow,
    isLoading,
    rolesDisabled,
    keyDepositDisabled,
    detailsReadOnly,
    detailsLocked,
    safeNrReadOnly,
    canConfigureSafeDepositBoxDetails,
    showProductClosureWarning,
    showServiceRoleValidationWarning,
    serviceRoleErrors,
    details,
    setDetail,
    safeLocationOptions,
    safeSizeOptions,
    configurationErrors,
    canEditSafeDepositBoxLocation,
  } = useSafeDepositBoxServiceRoleSection({ serviceAgreement, onSaveStateChange });

  const selectedPartnersContent = isLoading ? (
    <ZfBox
      data-testid="safe-deposit-box-service-roles-loading"
      style={{ display: 'flex', justifyContent: 'center', padding: 'var(--zf-spacing-medium, 16px)' }}
    >
      <ZfLoadingSpinner size="medium" />
    </ZfBox>
  ) : (
    <>
      {/* Sits next to the grid: the dialog scrolls, so a banner higher up can be off-screen. */}
      {showServiceRoleValidationWarning && (
        <ConfigureProductModalBanner
          testId="safe-deposit-box-service-role-validation-warning"
          type="warning"
          titleText={t('pb.configureproductmodal.safedepositbox.validation.warning.title')}
          description={t('pb.configureproductmodal.safedepositbox.validation.warning.description')}
        />
      )}
      <SafeDepositBoxServiceRolesGrid
        rows={rows}
        safeRoleOptions={safeRoleOptions}
        signatureAuthorityOptions={signatureAuthorityOptions}
        disabled={rolesDisabled}
        serviceRoleErrors={serviceRoleErrors}
        onChange={updateRow}
        onRemove={removeRow}
      />
    </>
  );

  return (
    <>
      {showProductClosureWarning && (
        <ConfigureProductModalBanner
          testId="product-closure-warning"
          type="warning"
          titleText={t('pb.configureproductmodal.safedepositbox.closure.warning.title')}
          description={t('pb.configureproductmodal.safedepositbox.closure.warning.description')}
        />
      )}
      {/*
        Must stay inside this section: ConfigureProductModal holds a single saveState that each
        section overwrites via onSaveStateChange, so promoting this to a sibling section would
        clobber the service-role save state.
      */}
      <SafeDepositBoxConfigurationSection
        details={details}
        safeLocationOptions={safeLocationOptions}
        safeSizeOptions={safeSizeOptions}
        detailsReadOnly={detailsReadOnly}
        detailsLocked={detailsLocked}
        safeNrReadOnly={safeNrReadOnly}
        canConfigureSafeDepositBoxDetails={canConfigureSafeDepositBoxDetails}
        keyDepositDisabled={keyDepositDisabled}
        errors={configurationErrors}
        onChange={setDetail}
        canEditSafeDepositBoxLocation={canEditSafeDepositBoxLocation}
      />
      <section
        data-testid="safe-deposit-box-partners-section"
        aria-label={t('pb.configureproductmodal.safedepositbox.partners.title')}
      >
        <ZfDividerTitle label={t('pb.configureproductmodal.safedepositbox.partners.title')} addTopMargin />
        <PartnerSearchWidget
          brUid={brUid}
          selectedPartners={selectedPartners}
          onSelectedPartnersChange={handleSelectedPartnersChange}
          disabled={rolesDisabled}
          allowAllPartnersSearch
          serviceDefinitionId={serviceAgreement.serviceDefinitionId}
          productName={getProductDisplayName(serviceAgreement)}
          selectedPartnersContent={selectedPartnersContent}
        />
      </section>
    </>
  );
};
